import { Translation, TranslocoConfig, TranslocoService } from '@jsverse/transloco';
import { firstValueFrom } from 'rxjs';

import { readStoredLanguage } from './preferences/preferences-storage';

/**
 * Resolve e ativa o idioma do bootstrap, na ordem: preferência gravada, idioma do navegador,
 * `defaultLang` do Transloco.
 *
 * 🔴 A preferência gravada vem PRIMEIRO porque ela é a escolha explícita do usuário — pelo seletor
 * de idioma ou pelo que veio no token do login. Antes desta ordem a função só olhava
 * `navigator.language`, então um F5 devolvia o app ao idioma do navegador e a escolha do usuário
 * era descartada a cada carga (a doc já dizia ler o localStorage; o código não lia).
 *
 * O idioma do token continua vencendo: `AuthService.checkLanguage` chama `setActiveLang` depois da
 * resposta do login, e a gravação da preferência acompanha a troca.
 *
 * @param translocoService A instância do TranslocoService.
 * @param translocoConfig A configuração do Transloco (obtida via TRANSLOCO_CONFIG).
 * @returns Promise que resolve com as traduções carregadas, garantindo que o Transloco esteja pronto.
 */
export function initializeTranslocoLanguage(translocoService: TranslocoService, translocoConfig: TranslocoConfig): Promise<Translation> {
  const availableLangIds = (translocoConfig.availableLangs || []).map((lang) => (typeof lang === 'string' ? lang : lang.id));

  let activeLang = Array.isArray(translocoConfig.defaultLang) ? translocoConfig.defaultLang[0] : translocoConfig.defaultLang;

  // Idioma do navegador: 'pt' de 'pt-BR'. Só vale se a aplicação carregar esse bundle.
  const shortBrowserLang = navigator.language.split('-')[0];

  if (shortBrowserLang.length > 1 && availableLangIds.includes(shortBrowserLang)) {
    activeLang = shortBrowserLang;
  }

  activeLang = readStoredLanguage(availableLangIds) ?? activeLang;

  translocoService.setActiveLang(activeLang);

  return firstValueFrom(translocoService.load(activeLang));
}
